import { useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import { Image, RenderBlocks, UniversalLink } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import {
  getVocabularyTitle,
  getVocabularyToken,
} from 'volto-ministerio-publico-federal/components/UI';
import { MpfNewsByThemeBlockView } from 'volto-ministerio-publico-federal/components/Blocks/NoticiasPorTema/View';
import { DEFAULT_LIMIT } from 'volto-ministerio-publico-federal/components/Blocks/NoticiasPorTema/schema';
import { type NewsViewProps } from './types';
import RelatedNews, { hasRelatedContent } from './RelatedItems';
import Attachments from 'volto-ministerio-publico-federal/components/Attachments';
import useFormattedDate from 'volto-ministerio-publico-federal/hooks/useFormattedDate';
import { buildNewsFilterUrl } from 'volto-ministerio-publico-federal/hooks/useNewsFilters';
import {
  formatReadingTime,
  getReadingTimeMinutes,
} from 'volto-ministerio-publico-federal/utils/readingTime';
import {
  NewsExclusionsProvider,
  useNewsExclusions,
  useRegisterExclusions,
} from './NewsExclusionsContext';
import NewsSidebar from './Sidebar';
import NewsSummary from './Summary';
import ShareBar from './ShareBar';
import {
  StateRule,
  useWorkflowState,
} from 'volto-ministerio-publico-federal/components/WorkflowState';
import {
  AvisoMarcacoes,
  TextoMarcado,
  useMarcacoesRevisao,
} from './MarcacoesRevisao';

const NewsViewBody = ({ content }: NewsViewProps) => {
  const {
    title,
    description,
    effective,
    modified,
    unidadeOrigem,
    image,
    creditoImagem,
    descricaoImagem,
    subjects,
    tema,
    items,
    relatedItems,
    image_caption,
  } = content;

  const workflowState = useWorkflowState(content.review_state);
  const { formatShortWithTime, formatRelative } = useFormattedDate();

  // Marca-texto do que os revisores alteraram (só para quem trabalha na notícia).
  const revisao = useMarcacoesRevisao(content);
  const marcacoes = revisao.visivel ? revisao.marcacoes : null;

  const formatPloneDate = (dateString?: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return formatShortWithTime(date);
  };

  const previewScale = content.preview_image_link?.image_scales?.image?.[0];
  // A API devolve a imagem com o endereço do backend; na renderização no
  // servidor esse endereço é o interno (plone-backend:8080), que o navegador
  // não alcança. Os dois caminhos da imagem saem relativos, como o do preview.
  const ownImageSrc = image?.scales?.large?.download || image?.download;
  const featuredImageSrc = ownImageSrc
    ? flattenToAppURL(ownImageSrc)
    : previewScale && content.preview_image_link?.['@id']
      ? flattenToAppURL(
          `${content.preview_image_link['@id']}/${
            previewScale.scales?.large?.download || previewScale.download
          }`,
        )
      : undefined;

  const assinaturaHtml =
    content['@components']?.assinatura?.data?.assinatura?.data;

  const resumoHtml = content.resumo?.data;

  const readingTime = useMemo(
    () =>
      formatReadingTime(
        getReadingTimeMinutes({
          blocks: content.blocks,
          blocks_layout: content.blocks_layout,
          description,
          resumo: resumoHtml,
        }),
      ),
    [content.blocks, content.blocks_layout, description, resumoHtml],
  );

  const [updatedRelative, setUpdatedRelative] = useState('');
  useEffect(() => {
    if (modified) {
      setUpdatedRelative(formatRelative(modified));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modified]);

  const temRelacionados = hasRelatedContent(relatedItems);

  useRegisterExclusions(
    'relacionados',
    temRelacionados ? (relatedItems || []).map((item) => item['@id']) : [],
  );
  const { register } = useNewsExclusions();

  const temaTitulo = getVocabularyTitle(tema);
  const temaToken = getVocabularyToken(tema);
  const unidadeToken = getVocabularyToken(unidadeOrigem);

  const temaBlockData = useMemo(
    () => ({
      '@type': 'mpfNoticiasPorTema',
      title: `Mais sobre ${temaTitulo}`,
      tema: temaToken,
      unidade: unidadeToken,
      limit: DEFAULT_LIMIT,
      linkMode: 'auto' as const,
    }),
    [temaTitulo, temaToken, unidadeToken],
  );

  const tags = useMemo(() => {
    const lista: Array<{ kind: string; label: string; href: string }> = [];

    if (temaTitulo && temaToken) {
      lista.push({
        kind: 'tema',
        label: temaTitulo,
        href: buildNewsFilterUrl({ tema: temaToken }),
      });
    }

    const unidadeTitulo = getVocabularyTitle(unidadeOrigem);
    if (unidadeTitulo && unidadeToken) {
      lista.push({
        kind: 'unidade',
        label: unidadeTitulo,
        href: buildNewsFilterUrl({ unidade: unidadeToken }),
      });
    }

    (subjects || []).forEach((tag) => {
      lista.push({
        kind: 'subject',
        label: tag,
        href: `/search?Subject=${encodeURIComponent(tag)}`,
      });
    });

    return lista;
  }, [temaTitulo, temaToken, unidadeOrigem, unidadeToken, subjects]);

  const anexos =
    items?.filter(
      (item) => item['@type'] === 'File' || item.mime_type?.includes('pdf'),
    ) || [];

  const OMIT_BODY_BLOCK_TYPES = ['title', 'description'];
  const omittedBlockIds = new Set(
    Object.entries(content.blocks || {})
      .filter(([, block]) =>
        OMIT_BODY_BLOCK_TYPES.includes(block?.['@type'] as string),
      )
      .map(([id]) => id),
  );

  const contentWithoutTitleAndDescription = { ...content };
  delete contentWithoutTitleAndDescription.title;
  delete contentWithoutTitleAndDescription.description;
  contentWithoutTitleAndDescription.blocks = Object.fromEntries(
    Object.entries(marcacoes?.blocks || content.blocks || {}).filter(
      ([id]) => !omittedBlockIds.has(id),
    ),
  );
  const layout = marcacoes?.blocks_layout || content.blocks_layout;
  contentWithoutTitleAndDescription.blocks_layout = {
    ...layout,
    items: (layout?.items || []).filter((id) => !omittedBlockIds.has(id)),
  };

  return (
    <section className="mpf-news">
      <article className="mpf-news__grid">
        {/* O cabeçalho mora dentro da coluna principal, e não como irmão do
            grid: no Figma ele ocupa só a largura da coluna principal, lado a
            lado com a lateral desde o topo — não a largura total da página
            acima das duas colunas. */}
        <div className="mpf-news__main-column">
          <header className="mpf-news__header">
            <div className="mpf-news__header-body">
              <div className="mpf-news__actions-wrapper">
                <UniversalLink
                  href={
                    content?.parent?.['@id']
                      ? flattenToAppURL(content.parent['@id'])
                      : '/noticias'
                  }
                  className="mpf-news__btn-secondary"
                >
                  <svg
                    aria-hidden="true"
                    className="mpf-news__btn-icon"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M19 12H5" />
                    <path d="M12 19l-7-7 7-7" />
                  </svg>
                  Voltar para {content?.parent?.title || 'Notícias'}
                </UniversalLink>
              </div>

              {revisao.marcacoes && (
                <AvisoMarcacoes
                  marcacoes={revisao.marcacoes}
                  desde={revisao.desde}
                  decisao={revisao.decisao}
                  atualizacao={revisao.atualizacao}
                  visivel={revisao.visivel}
                  alternar={revisao.alternar}
                />
              )}

              {title && (
                <h1 className="mpf-news__title">
                  {marcacoes?.titulo ? (
                    <TextoMarcado trechos={marcacoes.titulo} />
                  ) : (
                    title
                  )}
                </h1>
              )}

              {description && (
                <p className="mpf-news__subtitle">
                  {marcacoes?.descricao ? (
                    <TextoMarcado trechos={marcacoes.descricao} />
                  ) : (
                    description
                  )}
                </p>
              )}

              {workflowState ? (
                <div className="mpf-news__divider mpf-news__divider--state">
                  <StateRule info={workflowState} />
                </div>
              ) : (
                <hr className="mpf-news__divider" />
              )}

              <div className="mpf-news__meta-row">
                <div className="mpf-news__meta">
                  <span className="mpf-news__meta-item">
                    <svg
                      aria-hidden="true"
                      className="mpf-news__meta-icon"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    <strong className="mpf-news__meta-label">Autor (a)</strong>
                    <span>
                      {unidadeOrigem?.title ||
                        'Procuradoria-Geral da República'}
                    </span>
                  </span>

                  <span className="mpf-news__meta-item">
                    <svg
                      aria-hidden="true"
                      className="mpf-news__meta-icon"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 7v5l3 2" />
                    </svg>
                    <strong className="mpf-news__meta-label">Data:</strong>
                    <span>{formatPloneDate(effective)}</span>
                    {updatedRelative && (
                      <>
                        <span
                          aria-hidden="true"
                          className="mpf-news__related-meta-separator"
                        >
                          •
                        </span>
                        <span title={formatShortWithTime(modified)}>
                          {`Atualizado ${updatedRelative}`}
                        </span>
                      </>
                    )}
                  </span>

                  <span className="mpf-news__meta-item">
                    <svg
                      aria-hidden="true"
                      className="mpf-news__meta-icon"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 7v13" />
                      <path d="M3 5h5a4 4 0 0 1 4 2 4 4 0 0 1 4-2h5v13h-5a4 4 0 0 0-4 2 4 4 0 0 0-4-2H3z" />
                    </svg>
                    <strong className="mpf-news__meta-label">
                      Tempo estimado de leitura:
                    </strong>
                    <span>{readingTime}</span>
                  </span>
                </div>

                <ShareBar title={title || ''} />
              </div>
            </div>
          </header>

          <NewsSummary html={resumoHtml} />

          {featuredImageSrc && (
            <section className="mpf-news__featured-image">
              <div className="mpf-news__image-wrapper">
                <Image
                  className="mpf-news__img"
                  loading="eager"
                  src={featuredImageSrc}
                  alt={descricaoImagem ?? ''}
                />
              </div>

              {(creditoImagem || image_caption) && (
                <p className="mpf-news__image-credit">
                  {creditoImagem || image_caption}
                </p>
              )}
            </section>
          )}

          <section id="corpo-noticia" className="mpf-news__content">
            <div className="mpf-news__body-wrapper">
              <div className="mpf-news__prose">
                <RenderBlocks content={contentWithoutTitleAndDescription} />

                {assinaturaHtml && (
                  <div
                    className="mpf-news__signature"
                    dangerouslySetInnerHTML={{ __html: assinaturaHtml }}
                  />
                )}
              </div>

              {anexos && (
                <Attachments
                  items={anexos}
                  title="Anexos"
                  className="mpf-news__attachments"
                />
              )}
            </div>

            {tags.length > 0 && (
              <div className="mpf-news__footer-tags">
                <p className="mpf-news__tags-label">Tags:</p>
                <ul className="mpf-news__tags-list">
                  {tags.map((tag) => (
                    <li key={`${tag.kind}-${tag.label}`}>
                      <UniversalLink
                        href={tag.href}
                        className={cx('mpf-news__tag', {
                          'mpf-news__tag--unidade': tag.kind === 'unidade',
                        })}
                      >
                        {tag.label}
                      </UniversalLink>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {temRelacionados ? (
              <RelatedNews
                relatedItems={relatedItems}
                parent={content.parent}
              />
            ) : temaToken ? (
              <MpfNewsByThemeBlockView
                data={temaBlockData}
                block="news-view-tema"
                excludeId={content['@id']}
                onItemsRendered={(ids) => register('tema', ids)}
                sectionHeading="Veja também"
                cardBaseClass="mpf-news__related"
              />
            ) : null}
          </section>
        </div>

        <NewsSidebar unidade={unidadeToken} />
      </article>
    </section>
  );
};

export const NewsView = ({ content }: NewsViewProps) => (
  <NewsExclusionsProvider currentId={content?.['@id']}>
    <NewsViewBody content={content} />
  </NewsExclusionsProvider>
);

export default NewsView;
